/**
 * Module: web/src/types/api.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.23.0 (decision #210 -- assisted-entry-api v1.5.0:
 *   AssistedDraftDetail.dispatcher_options picker vocabulary; MyEntry
 *   entered_for / entered_by_name back-entry chip fields)
 * Date: 2026-07-28
 *
 * v1.18.0: the /review split (contract entry-log-api v1.0.0, AE slice 4,
 * decisions #176 + #177) -- ScreenedOutCall (UnenteredCall + the screening
 * why-chip object) + ScreenedOutResponse (paginated); EntryLogEntry /
 * EntryLogUser / EntryLogResponse for the manager register; MoEntryDetail
 * for the shared Entry Detail modal's GET /mo-entries/{id} read (the
 * _DIFF_COLS business fields + meta + void state); + entry_log_view
 * capability key (manager + admin rows, migration 0024).
 *
 * v1.17.0: AE reviewer context pack (contract assisted-entry-api v1.2.0,
 * additive) -- AssistedDraftDetail gains the calls-parent journey context
 * (journey / path / caller_outcome / direction / call_started_at /
 * call_duration_seconds; shapes re-served verbatim from the call-history
 * types -- one vocabulary, never forked) + AssistedDraftSort for the SS1
 * queue sort vocabulary (priority | newest | oldest | est_lost).
 *
 * v1.16.0: + call_forensics capability key (CH-2 phase 2, contract
 * call-history-api v1.6.0 sect 1 / decision #172 -- admin-row only; gates
 * the Forensic modal tab + GET /call-history/calls/{id}/forensics).
 *
 * v1.15.0: Module Settings (contract module-settings-api v1.0.0, additive)
 * -- ModuleSettingItem / ModuleSettingsAdminResponse (admin GET/PUT) +
 * ClientModuleSettings (the any-authed client_visible values feed);
 * + module_settings capability key (admin-row only).
 *
 * v1.14.0: audit rollback (contract audit-rollback-api v1.0.0, additive) --
 * AuditEvent gains rollback {eligible, reason?} (static eligibility) +
 * rolled_back_by (provenance annotation); new RollbackPreview /
 * RollbackResult for GET rollback-preview / POST rollback.
 *
 * v1.13.0: error observability shapes (contract error-observability-api
 * v1.0.0 §7, additive) -- AppErrorRow + AdminErrorsResponse for the admin
 * Errors view over GET /admin/errors (client beacon + server middleware rows
 * interleaved).
 *
 * v1.12.0: waived payment state (contract v1.7.0, decisions #168/#169,
 * additive) -- ReviewMention gains waived_at/waived_by; ReviewDriver +
 * RosterPerson gain waived; ReviewMentionReportRow gains waived_at;
 * ReviewDriverSummaryRow gains optional waived. Unpaid everywhere now
 * EXCLUDES waived (paid_at IS NULL AND waived_at IS NULL server-side).
 *
 * v1.11.0: roster role labels (contract v1.6.0 §3A) -- RosterRoleLabel
 * vocabulary + RosterPerson.role_labels; create/update bodies carry
 * role_labels (the Dispatcher label derives is_dispatcher server-side, so
 * the bodies drop their is_dispatcher field).
 *
 * TypeScript shapes for the operations portal API contract (doc 04 sect 6 + 9.1;
 * doc 05 for the MO entry-form endpoints; decision #122 for the Manager
 * Review coverage + transcript endpoints; the locked assisted-entry-api
 * contract for /api/assisted-entry/*; the locked review-incentives-api
 * contract for /api/review-incentives/*). The backend (FastAPI, sibling
 * package) implements exactly this contract; the SPA codes against it.
 * All errors are JSON { code, detail? }.
 *
 * v1.3.0: Assisted-Entry Review Queue shapes (P6) -- draft summaries/detail,
 * history events, dismiss enum, the {fields} edit-promote body.
 * v1.4.0: Review Incentives shapes (P9, contract review-incentives-api
 * v1.0.0 sect 3 + 5) -- queue list/detail, mentions, suggestions, driver
 * roster, mention-report rows; + review_incentives_manage capability key.
 * v1.5.0: P9.2 persisted-suggestion fields (contract v1.1.0 sect 5) --
 * ReviewSuggestion evidence/confidence/source, detail suggestions_source +
 * attribution_judged_at.
 * v1.6.0: roster-management shapes (contract v1.2.0 sect 3A, decision #143)
 * -- RosterPerson + create/update bodies.
 * v1.7.0: + call_history_view capability key (P10 Call History, contract
 * call-history-api v1.0.0 sect 1 -- manager-row only). Module shapes live in
 * types/callHistory.ts.
 * v1.8.0: P11 attribution ride-along for the Manager Review surface
 * (routes.review 1.1.0, additive): UnenteredCall gains answered_by_name /
 * answered_by_extension / caller_name (CNAM) / direction / leg_count (the
 * queue is now ONE CARD PER CALL -- id points at the canonical leg);
 * TranscriptUtterance gains role (SS5.5 rule) + confidence;
 * RecordingTranscript gains answered_by_name + direction.
 * v1.9.0: P12 System Config shapes (locked contract system-config-api
 * v1.0.0 sect 5/6, decision #146) -- masked provider/key state, AI
 * procedure matrix, curated model list, apply effectiveness report, GoTo
 * OAuth wizard payloads; + system_config capability key (admin-row only).
 * v1.9.1: contract v1.0.1 additive alignment (sect 3.2 build notes):
 * provider.drift is boolean|null (null = never rendered); goto last_verify
 * carries the FULL sect 6.5 result ({at, ok, granted_scope?, families?}).
 * v1.10.0: P9.4 pending-suggestion visibility (contract review-incentives-api
 * v1.4.0 sect 3/3A, additive) -- ReviewDriver + RosterPerson gain
 * suggested_pending, ReviewDriverSummaryRow gains optional suggested_pending
 * (unconfirmed AI suggestions on pending reviews; advisory, never counted
 * as mentions).
 */

// v1.17.0: the AE draft detail re-serves the calls-parent journey context
// with the CALL-HISTORY shapes verbatim (contract assisted-entry-api v1.2.0
// SS5 cites call-history-api SS5.4a/b as the source of truth -- one
// vocabulary, never forked).
import type { CallDirection, CallJourney, CallPath } from './callHistory';

/** Capability machine keys (operations.capabilities, doc 04 sect 3.2 + PIN allow-list). */
export type Capability =
  | 'reporting_view'
  | 'data_entry'
  | 'assisted_entry_review'
  | 'recordings_listen'
  | 'review_incentives_manage'
  | 'call_history_view'
  | 'call_forensics'
  | 'entry_log_view'
  | 'performance_view'
  | 'engine_performance_view'
  | 'system_config'
  | 'module_settings'
  | 'user_admin'
  | 'role_admin'
  | 'calendar'; // v2-deferred; valid inside a PIN scope only in v1

/** Role machine keys (operations.roles). A user holds a SET of these. */
export type RoleKey = 'admin' | 'manager' | 'dispatcher' | 'driver';

/** The four caps a manager may override per-user in v1 (doc 04 sect 9.1). */
export const OVERRIDABLE_CAPS: Capability[] = [
  'data_entry',
  'reporting_view',
  'assisted_entry_review',
  'recordings_listen',
];

/** The low-risk PIN scope allow-list (doc 04 sect 3.11). The UI offers nothing else. */
export const PIN_SCOPE_ALLOWLIST: Capability[] = ['calendar', 'data_entry'];

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  roles: string[]; // role keys (SET semantics -- union of caps server-side)
  person_id: number | null;
}

export interface PersonRef {
  id: number;
  name: string;
}

/** Full-credential session shape (login response + whoami for full sessions). */
export interface FullSession {
  user: AuthUser;
  effective_caps: string[];
  must_change_password: boolean;
}

/** PIN partial session shape (pin-login response + whoami for PIN sessions). */
export interface PinSession {
  session_kind: 'pin';
  person: PersonRef;
  pin_caps: string[];
}

export type WhoamiResponse = FullSession | PinSession;

export function isPinSession(resp: WhoamiResponse): resp is PinSession {
  return 'session_kind' in resp && resp.session_kind === 'pin';
}

// ---------------------------------------------------------------------------
// My entries (driver / read-only landing)
// ---------------------------------------------------------------------------

export interface MyEntry {
  id: number;
  call_date: string;
  call_time: string;
  service: string | null;
  duty: string | null;
  /** Contract leaves the exact type open -- render boolean as Yes/No, number as $, string verbatim. */
  quote_given: boolean | number | string | null;
  reasons: string[];
  /** v1.23.0 (decision #210 B): dispatcher's name when YOU recorded this for someone else. */
  entered_for: string | null;
  /** v1.23.0 (decision #210 B): recorder's name when someone else entered this under YOUR attribution. */
  entered_by_name: string | null;
}

export interface MyEntriesResponse {
  entries: MyEntry[];
}

// ---------------------------------------------------------------------------
// Admin -- users / roles / overrides
// ---------------------------------------------------------------------------

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  roles: string[];
  active: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  person_id: number | null;
  goto_extension?: string | null;
}

export type OverrideEffect = 'grant' | 'deny';

export interface CapabilityOverride {
  capability: string;
  effect: OverrideEffect;
}

/** GET /admin/users/{id} -- user + overrides + effective caps. */
export interface AdminUserDetail {
  user: AdminUser;
  overrides: CapabilityOverride[];
  effective_caps: string[];
}

export interface AdminRole {
  id: number;
  key: string;
  label: string;
  description?: string | null;
  capabilities: string[]; // the role's default capability set
}

export interface AdminPerson {
  id: number;
  name: string;
  active?: boolean;
  is_dispatcher?: boolean;
  is_driver?: boolean;
  has_account?: boolean;
}

// ---------------------------------------------------------------------------
// Admin -- PINs / audit
// ---------------------------------------------------------------------------

export interface AdminPin {
  id: number;
  person_id: number;
  person_name?: string;
  person?: PersonRef;
  scope: string[];
  active?: boolean;
  issued_by?: string | number | null;
  issued_at?: string | null;
  expires_at?: string | null;
  last_used_at?: string | null;
}

/** POST /admin/pins -> the 4 digits, shown exactly once. */
export interface PinIssueResponse {
  pin: string;
}

export interface AuditEvent {
  id: number;
  at: string;
  event: string;
  user_id?: number | null;
  email_tried?: string | null;
  ip?: string | null;
  detail?: Record<string, unknown> | string | null;
  /** v1.14.0 (contract audit-rollback-api §6): STATIC undo eligibility --
   *  matrix class + recorded-detail sufficiency. The live precondition is
   *  the preview endpoint's job. */
  rollback?: { eligible: boolean; reason?: string | null };
  /** Newest audit_rollback row id targeting this row (reversal-not-erasure
   *  annotation), or null. */
  rolled_back_by?: number | null;
}

// ---------------------------------------------------------------------------
// Admin -- audit rollback (contract audit-rollback-api v1.0.0 §7)
// ---------------------------------------------------------------------------

/** GET /admin/audit/{id}/rollback-preview -- dry run, never mutates. */
export interface RollbackPreview {
  audit_id: number;
  event: string;
  /** Static matrix/detail class AND the live precondition combined. */
  eligible: boolean;
  reason: string | null;
  /** Machine verb ('unpay', 'restore_review_state_and_mentions', ...). */
  action: string | null;
  /** The confirm dialog renders this verbatim -- exactly what will happen. */
  description: string | null;
  target: {
    kind: 'review_mention' | 'review' | 'user' | 'mo_entry' | (string & {});
    id: number;
    review_id?: number;
    current: Record<string, unknown>;
  } | null;
  rolled_back_by: number | null;
}

/** POST /admin/audit/{id}/rollback -- the executed reversal. */
export interface RollbackResult {
  /** The new audit_rollback provenance row (list-item shape). */
  rollback: AuditEvent;
  /** The refreshed target block (§7.1 shape). */
  target: NonNullable<RollbackPreview['target']>;
}

// ---------------------------------------------------------------------------
// Admin -- error observability (contract error-observability-api v1.0.0 §7)
// ---------------------------------------------------------------------------

export type AppErrorSource = 'client' | 'server';

/** §3 kind vocabulary -- open set server-side, so `string` stays the base. */
export type AppErrorKind =
  | 'code_error'
  | 'unhandled_rejection'
  | 'api_error'
  | 'request_failed'
  | 'http_4xx'
  | 'http_5xx'
  | 'exception'
  | (string & {});

/** One operations.app_errors row (client beacon OR server middleware). */
export interface AppErrorRow {
  id: number;
  /** Client-reported capture time on beacon rows; server time on middleware rows. */
  occurred_at: string;
  /** Server insert time -- the trustworthy timestamp. */
  received_at: string;
  source: AppErrorSource;
  kind: AppErrorKind;
  user_id: number | null;
  user_name: string | null;
  session_kind: 'full' | 'pin' | null;
  /** SPA route path at capture (client rows); null on server rows. */
  page: string | null;
  /** What the user was doing -- the reportCaughtError label. */
  interaction: string | null;
  message: string;
  /** Stack excerpt / status / method+path / ua / app shell / burst count. */
  detail: Record<string, unknown> | null;
  client_error_id: string | null;
}

/** GET /admin/errors envelope -- counts describe the FILTERED set (§7). */
export interface AdminErrorsResponse {
  errors: AppErrorRow[];
  total: number;
  page: number;
  page_size: number;
  counts: {
    source: Partial<Record<AppErrorSource, number>>;
    kind: Record<string, number>;
  };
}

// ---------------------------------------------------------------------------
// MO entry form -- the /dispatch wizard (doc 05 sect 3, 4, 6, 8, 11)
// ---------------------------------------------------------------------------

/** customer_type enum (doc 05 sect 4.4, Decision D6). */
export type MoCustomerType = 'NON_ACCOUNT' | 'ACCOUNT' | 'UNKNOWN';

export interface MoBootstrapAccount {
  id: number;
  label: string;
  account_type: string | null;
}

export interface MoBootstrapServiceType {
  id: number;
  code: string;
  sort: number | null;
}

export interface MoBootstrapDutyType {
  id: number;
  code: string;
  sort: number | null;
}

export interface MoBootstrapReason {
  id: number;
  code: string;
  is_sentinel: boolean;
}

export interface MoBootstrapPerson {
  id: number;
  full_name: string;
}

/**
 * GET /mo-form/bootstrap -- every vocabulary + session defaults in ONE call,
 * cached client-side (doc 05 sect 11). `people` is non-null ONLY for sessions
 * allowed to override the dispatcher attribution (manager back-entry).
 */
export interface MoFormBootstrap {
  accounts: MoBootstrapAccount[];
  service_types: MoBootstrapServiceType[];
  duty_types: MoBootstrapDutyType[];
  /** Grouped by group name, e.g. { price: [...], sentinel: [...] } (doc 05 sect 4.3). */
  reasons: Record<string, MoBootstrapReason[]>;
  people: MoBootstrapPerson[] | null;
  defaults: {
    dispatcher_person_id: number | null;
    /** ISO timestamp -- anchors the date/time defaults to the SERVER clock. */
    server_now: string;
  };
}

/**
 * POST /geocode response (doc 05 sect 6.2-6.3). confidence 'unavailable' means
 * the lookup path is down (degrade-open: banner + manual pin, NEVER blocks
 * submit); 'no_match' means the address text resolved to nothing.
 */
export interface GeocodeResult {
  lat: number | null;
  lon: number | null;
  canonical_address: string | null;
  confidence: string; // rooftop|range_interpolated|geometric_center|approximate|no_match|unavailable
  place_id: string | null;
  provider?: string | null;
  cached?: boolean;
}

/** One candidate from GET /recordings/match?phone=&at= (doc 05 sect 8.2). */
export interface RecordingMatch {
  id: string | number;
  goto_call_id: string | null;
  started_at: string;
  duration_seconds: number | null;
  dispatcher_extension: string | null;
  ai_summary: string | null;
  ai_sentiment: string | null;
  has_audio: boolean;
  transcribed: boolean;
}

export interface RecordingMatchResponse {
  matches: RecordingMatch[];
}

/**
 * POST /mo-entries body. Client validation is UX ONLY (doc 05 sect 9) -- the
 * required selects are typed nullable so a payload can always be sent and the
 * server's authoritative 422 {errors:{field:msg}} maps back onto the form.
 */
export interface MoEntryPayload {
  dispatcher_person_id?: number;
  call_date: string;
  call_time: string;
  customer_type: MoCustomerType;
  account_id?: number;
  caller_company?: string;
  caller_name?: string;
  caller_phone?: string;
  caller_location?: string;
  pickup_lat?: number;
  pickup_lon?: number;
  pickup_canonical_address?: string;
  pickup_geocode_confidence?: string;
  pickup_place_id?: string;
  duty_type_id: number | null;
  service_type_id: number | null;
  eta_minutes?: number;
  mileage?: number;
  quote_given?: number;
  quote_note?: string;
  competitor_quote?: number;
  competitor_note?: string;
  callback_potential?: number;
  notes?: string;
  reasons: number[];
  recording_id?: string | number;
}

/** 201 response of POST /mo-entries. */
export interface MoEntryCreated {
  id: number;
}

// ---------------------------------------------------------------------------
// Manager Review -- coverage window + transcript (decision #122)
// ---------------------------------------------------------------------------

/** One committed entry in the review window (GET /review/coverage). */
export interface ReviewEntry {
  id: number;
  call_date: string | null;
  call_time: string | null;
  shift: string | null;
  dispatcher_name: string | null;
  caller_phone: string | null;
  caller_company: string | null;
  quote_given: number | string | null;
  reasons: string[];
  source: string;
  recording_id: string | null;
  submitted_at: string | null;
}

/**
 * One CALL with no entry linkage yet -- the review queue (v1.8.0: deduped by
 * goto_call_id server-side; `id` is the CANONICAL leg's recording id, so
 * audio/transcript/deep-link actions keep working unchanged and the
 * availability flags describe that leg).
 */
export interface UnenteredCall {
  id: string;
  goto_call_id: string | null;
  caller_phone: string | null;
  started_at: string;
  duration_seconds: number | null;
  dispatcher_extension: string | null;
  dispatcher_user_key: string | null;
  ai_summary: string | null;
  ai_sentiment: string | null;
  has_audio: boolean;
  /** Audio removed by the retention purge -- transcript retained (A3). */
  audio_purged: boolean;
  transcribed: boolean;
  /** v1.8.0: call-level attribution (migration 0010); null on legacy calls. */
  answered_by_extension: string | null;
  /** v1.8.0: answerer's portal display name; null when no user maps the ext. */
  answered_by_name: string | null;
  /** v1.8.0: CNAM ("JAMES PETERSON", "WIRELESS CALLER"); null pre-attribution. */
  caller_name: string | null;
  /** v1.8.0: inbound|outbound|internal; null = unknown/legacy. */
  direction: string | null;
  /** v1.8.0: recording legs collapsed into this card. */
  leg_count: number;
}

/** Extension filter vocabulary: every extension seen in the window. */
export interface ReviewExtension {
  extension: string;
  calls: number;
}

// ---------------------------------------------------------------------------
// Review split (AE slice 4 -- locked contract entry-log-api v1.0.0)
// ---------------------------------------------------------------------------

/**
 * The why-chip on a Screened Out card (contract SS2 resolution order):
 * newest dismissed draft > any-leg AI not_applicable > the honest neutral
 * class ("Not yet screened" -- unjudged backlog / transcript-less / error).
 */
export interface ScreeningVerdict {
  kind: 'dismissed' | 'ai_not_applicable' | 'none';
  dismiss_reason: string | null;
  /** Reviewer display name; null on kind='dismissed' = the SLA auto-expire. */
  dismissed_by_name: string | null;
  dismissed_at: string | null;
}

/** A Screened Out card: the coverage card + its screening verdict. */
export interface ScreenedOutCall extends UnenteredCall {
  screening: ScreeningVerdict;
}

export interface ScreenedOutResponse {
  calls: ScreenedOutCall[];
  extensions: ReviewExtension[];
  window: { hours: number; from: string; to: string };
  total: number;
  page: number;
  page_size: number;
}

/** One register row (contract SS3; voided rows INCLUDED, VOIDED pill). */
export interface EntryLogEntry {
  id: number;
  call_date: string | null;
  call_time: string | null;
  shift: string | null;
  dispatcher_person_id: number | null;
  dispatcher_name: string | null;
  caller_phone: string | null;
  caller_company: string | null;
  quote_given: number | string | null;
  reasons: string[];
  source: string;
  recording_id: string | null;
  goto_call_id: string | null;
  submitted_at: string | null;
  /** The credential that keyed the row -- differs from dispatcher on back-entry. */
  entered_by_name: string | null;
  voided_at: string | null;
  /** An 'edited' revision exists (the row's edited-indicator). */
  edited: boolean;
}

/** Users-filter vocabulary row (cap holders UNION people-with-entries). */
export interface EntryLogUser {
  person_id: number;
  name: string;
  entry_count: number;
}

export interface EntryLogResponse {
  entries: EntryLogEntry[];
  users: EntryLogUser[];
  window: { from: string; to: string };
  total: number;
  page: number;
  page_size: number;
}

/**
 * GET /api/mo-entries/{id} -- the shared Entry Detail modal's read (the
 * routes.entries edit-view payload: business columns + meta + void state;
 * reasons are IDs here, resolved to codes via the bootstrap vocabularies).
 */
export interface MoEntryDetail {
  id: number;
  dispatcher_person_id: number | null;
  call_date: string | null;
  call_time: string | null;
  shift: string | null;
  shift_derived: string | null;
  customer_type: string | null;
  account_id: number | null;
  caller_company: string | null;
  caller_name: string | null;
  caller_phone: string | null;
  caller_location: string | null;
  pickup_lat: number | string | null;
  pickup_lon: number | string | null;
  pickup_canonical_address: string | null;
  pickup_geocode_confidence: string | null;
  pickup_place_id: string | null;
  duty_type_id: number | null;
  service_type_id: number | null;
  eta_minutes: number | null;
  eta_raw: string | null;
  mileage: number | string | null;
  quote_given: number | string | null;
  quote_note: string | null;
  competitor_quote: number | string | null;
  competitor_note: string | null;
  callback_potential: number | null;
  notes: string | null;
  recording_id: string | null;
  goto_call_id: string | null;
  transcript_ref: string | null;
  submitted_at: string | null;
  source: string;
  created_at: string | null;
  updated_at: string | null;
  reasons: number[];
  voided_at: string | null;
  voided_by: number | null;
}

/** GET /review/coverage?hours=&extension= -- manager-tier only. */
export interface ReviewCoverageResponse {
  entries: ReviewEntry[];
  unentered_calls: UnenteredCall[];
  extensions: ReviewExtension[];
  window: { hours: number; from: string; to: string };
}

/** One speaker-labeled utterance; start/end are MILLISECONDS. */
export interface TranscriptUtterance {
  speaker: string | null;
  text: string | null;
  start: number | null;
  end: number | null;
  /**
   * v1.8.0: channel->role via the call-history SS5.5 first-utterance rule.
   * NULL on mono / internal / unknown-direction legs (UI falls back to
   * speaker letters).
   */
  role: 'dispatcher' | 'external' | null;
  /** v1.8.0: AAI utterance confidence 0..1; < 0.5 renders dimmed. */
  confidence: number | null;
}

// ---------------------------------------------------------------------------
// Assisted-Entry Review Queue (P6 -- locked contract assisted-entry-api v1.0)
// ---------------------------------------------------------------------------

export type DraftReviewState = 'pending' | 'approved' | 'edited' | 'dismissed' | 'superseded';

/**
 * Queue sort vocabulary (contract SS1 v1.2.0): `priority` = callback rank ->
 * lost revenue -> recency (default); `newest`/`oldest` on occurred_at;
 * `est_lost` = estimated_lost_revenue_usd DESC NULLS LAST -> recency.
 */
export type AssistedDraftSort = 'priority' | 'newest' | 'oldest' | 'est_lost';

/** The draft-side callback enum (the promoted entry gets the 1-10 bucket). */
export type DraftCallbackPotential = 'high' | 'medium' | 'low' | 'unclear';

/** Human dismiss reasons (contract SS1). 'stale' is SLA/system-written only. */
export type DismissReason =
  | 'not_a_missed_opp'
  | 'duplicate'
  | 'internal_call'
  | 'bad_extraction'
  | 'other';

/** Fuzzy phone+window match against a human entry -- never auto-dropped (doc 08 SS8). */
export interface DedupeHint {
  match_type?: string;
  entry_id?: number;
  draft_id?: number;
  seconds?: number;
  [k: string]: unknown;
}

/** Queue list item (contract SS5). */
export interface AssistedDraftSummary {
  id: number;
  occurred_at: string | null;
  caller_phone: string | null;
  company: string | null;
  account_label: string | null;
  duty: string | null;
  service: string | null;
  reason_primary: string | null;
  reasons: string[];
  quote_given_usd: number | string | null;
  callback_potential: DraftCallbackPotential | null;
  estimated_lost_revenue_usd: number | string | null;
  follow_up_recommended: boolean | null;
  dedupe_hint: DedupeHint | null;
  review_state: DraftReviewState;
  created_at: string;
}

/** GET /assisted-entry/drafts -- pending_count feeds the nav badge. */
export interface AssistedDraftListResponse {
  drafts: AssistedDraftSummary[];
  pending_count: number;
}

/** Contract SS5 transcript utterance shape (start_ms, not start/end).
 * v1.4.1: + channel/role -- the SS5.5 direction-aware channel->role map
 * (role NULL on mono/internal/unknown-direction legs). */
export interface DraftUtterance {
  speaker: string | null;
  channel: string | null;
  role: 'dispatcher' | 'external' | null;
  start_ms: number | null;
  text: string | null;
}

/** One mo_draft_history event (append-only audit trail). */
export interface DraftHistoryEvent {
  at: string;
  action: 'drafted' | 'edited' | 'approved' | 'dismissed' | 'superseded' | 'reopened';
  from_state: string | null;
  to_state: string | null;
  /** Actor display name; null = the drafter / SLA system. */
  actor: string | null;
  field_diffs: Record<string, { from: unknown; to: unknown }> | null;
  note: string | null;
}

/**
 * GET /assisted-entry/drafts/{id} (contract SS5 + the v1.0.1 additive
 * editor/wizard-hydration fields). `transcript` rides ONLY when the caller
 * also holds recordings_listen; the evidence quote is always present.
 */
export interface AssistedDraftDetail extends AssistedDraftSummary {
  eta_minutes: number | null;
  mileage_miles: number | string | null;
  pickup_address: string | null;
  pickup_lat: number | string | null;
  pickup_lon: number | string | null;
  competitor_named: string | null;
  competitor_quote_usd: number | string | null;
  mo_reason_evidence: string | null;
  callback_potential_reasoning: string | null;
  recording_id: string | null;
  goto_call_id: string | null;
  llm_model: string | null;
  llm_generated_at: string | null;
  reasoning_artifact_available: boolean;
  history: DraftHistoryEvent[];
  // additive editor/wizard-hydration fields (contract v1.0.1)
  dispatcher_person_id: number | null;
  dispatcher_name: string | null;
  /** v1.23.0 (decision #210 E, contract v1.5.0): reviewer-attribution picker
   *  vocabulary -- non-null ONLY when the draft is UNASSIGNED. */
  dispatcher_options: Array<{ id: number; full_name: string }> | null;
  customer_type: MoCustomerType;
  account_id: number | null;
  caller_name: string | null;
  duty_type_id: number | null;
  service_type_id: number | null;
  eta_raw_utterance: string | null;
  /** The enum mapped to the legacy 1-10 bucket (high 8 / medium 5 / low 2). */
  callback_score: number | null;
  reason_ids: number[];
  llm_runner: string | null;
  llm_prompt_version: string | null;
  dismiss_reason: string | null;
  promoted_entry_id: number | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  has_audio: boolean;
  transcript_available: boolean;
  transcript?: { utterances: DraftUtterance[] };
  // v1.17.0 -- the calls-parent journey context (contract v1.2.0 SS5;
  // shapes = call-history-api SS5.4a/b). ALWAYS present, null when the
  // draft has no goto_call_id or no parent row. hangup_by is deliberately
  // NOT served (calls-foundation SS1.3).
  /** SS5.4a event timeline (path popped out server-side); null = no data. */
  journey: CallJourney | null;
  /** SS5.4b Complete Path; null = outbound / pre-backfill / no parent. */
  path: CallPath | null;
  /** GoTo's own outcome classification (open enum, lowercased). */
  caller_outcome: string | null;
  direction: CallDirection | null;
  /** Parent started_at (callCreated -- true call start; occurred_at stays the judged-leg moment). */
  call_started_at: string | null;
  /** Parent conversation-space lifetime (lot occupancy on park-abandons). */
  call_duration_seconds: number | null;
}

/** POST /assisted-entry/drafts/{id}/approve|edit -> the promoted entry id. */
export interface DraftPromoted {
  entry_id: number;
  changed_fields?: string[];
}

/** GET /recordings/{id}/transcript (recordings_listen). 404 {code:'no_transcript'} when absent. */
export interface RecordingTranscript {
  id: string;
  language: string | null;
  duration_seconds: number | null;
  text: string;
  utterances: TranscriptUtterance[];
  ai_summary: string | null;
  ai_sentiment: string | null;
  /** v1.8.0: answerer's display name for the panel header; null on legacy legs. */
  answered_by_name: string | null;
  /** v1.8.0: inbound|outbound|internal; null = unknown (external label fallback). */
  direction: string | null;
}

// ---------------------------------------------------------------------------
// Review Incentives (P9 -- locked contract review-incentives-api v1.0.0)
// ---------------------------------------------------------------------------

/**
 * Review triage state (contract sect 2): 'skipped' = office manager marked
 * "No Driver Mentioned"; 'attributed' = >=1 mention row; 'pending' = untriaged.
 * Paid lives on the MENTION (per-driver), never on the review.
 */
export type ReviewAttributionState = 'pending' | 'attributed' | 'skipped';

/** One driver credit on a review (operations.review_mentions). */
export interface ReviewMention {
  id: number;
  person_id: number;
  full_name: string;
  /** NULL = attributed, awaiting payment (noon-UTC-of-date legacy convention when set). */
  paid_at: string | null;
  /** OPTIONAL bonus amount (decision #133) -- Postgres NUMERIC may arrive as string. */
  bonus_amount_usd: number | string | null;
  /**
   * Waived payment state (contract v1.7.0, decision #168): management
   * declined to pay while the attribution stands. Mutually exclusive with
   * paid_at (server 409s both directions). Unpaid = both NULL.
   */
  waived_at?: string | null;
  waived_by?: number | null;
}

/** Queue list item (contract sect 3 pinned list shape; excerpt capped at 200). */
export interface ReviewListItem {
  id: number;
  reviewer_name: string | null;
  star_rating: number;
  review_excerpt: string | null;
  review_date: string | null;
  location_name: string | null;
  attribution_state: ReviewAttributionState;
  mentions: ReviewMention[];
  review_link: string | null;
}

/** GET /review-incentives/reviews -- newest review_date first + state counts. */
export interface ReviewListResponse {
  reviews: ReviewListItem[];
  total: number;
  counts: { pending: number; attributed: number; skipped: number };
}

/**
 * Server-side attribution suggestion (contract sect 5 v1.1.0, persisted-preferred):
 * source 'llm' = the scheduled attribution tick's persisted row (verbatim evidence
 * quote + confidence; score mapped high=100/medium=85/low=70); source 'scorer' =
 * the legacy substring fallback for not-yet-judged reviews (full_name hit = 100,
 * name_variations[] hit = 80). ALWAYS advisory -- the office manager confirms
 * every attribution.
 */
export interface ReviewSuggestion {
  person_id: number;
  full_name: string;
  score: number;
  /** Scorer rows: the substring that hit (variation text, or the full name itself). */
  matched_on: string | null;
  /** LLM rows: verbatim quote from the review text (the UI highlights it). */
  evidence?: string | null;
  confidence?: 'high' | 'medium' | 'low' | null;
  source?: 'llm' | 'scorer';
}

/** GET /review-incentives/reviews/{id} -- full review text + suggestions ride here. */
export interface ReviewIncentiveDetail extends ReviewListItem {
  review_text: string | null;
  suggestions: ReviewSuggestion[];
  /** Which engine produced `suggestions` (contract sect 5 v1.1.0). */
  suggestions_source?: 'llm' | 'scorer' | null;
  /** Set when the scheduled attribution tick judged this review. */
  attribution_judged_at?: string | null;
}

/** GET /review-incentives/drivers roster row (people WHERE is_driver, active-first). */
export interface ReviewDriver {
  person_id: number;
  full_name: string;
  active: boolean;
  mentions: number;
  paid: number;
  /** v1.7.0 (decision #168): EXCLUDES waived — paid_at AND waived_at both NULL. */
  unpaid: number;
  /** Waived-mention count (contract v1.7.0, additive; 0 on older APIs). */
  waived: number;
  last_mention_at: string | null;
  /**
   * AI-suggested mentions still awaiting manager confirmation (contract
   * v1.4.0 §3): suggestions on PENDING-state reviews with no confirmed
   * mention. Advisory only — never included in `mentions`.
   */
  suggested_pending: number;
}

/**
 * GET /review-incentives/roster row (contract sect 3A, decision #143): ALL
 * operations.people rows -- identity, not credentials. has_account = an
 * ACTIVE login account links this person; mentions = review-mention count.
 */
/** Roster role LABEL vocabulary (contract v1.6.0 §3A -- display identity
 *  only; the Dispatcher label derives is_dispatcher server-side). */
export type RosterRoleLabel = 'driver' | 'dispatcher' | 'manager';

export interface RosterPerson {
  person_id: number;
  full_name: string;
  active: boolean;
  is_driver: boolean;
  is_dispatcher: boolean;
  notes: string | null;
  created_at: string | null;
  has_account: boolean;
  mentions: number;
  /** Unconfirmed AI suggestions (contract v1.4.0 §3 definition). */
  suggested_pending: number;
  /** Waived-mention count (contract v1.7.0 §3A, decision #168). */
  waived: number;
  /** Sorted label subset (contract v1.6.0 §3A). */
  role_labels: RosterRoleLabel[];
}

/** POST /review-incentives/roster body (is_driver defaults TRUE, #137). */
export interface RosterCreateBody {
  full_name: string;
  is_driver?: boolean;
  role_labels?: RosterRoleLabel[];
  notes?: string;
}

/** POST /review-incentives/roster/{id} body -- partial; >=1 field required. */
export interface RosterUpdateBody {
  full_name?: string;
  is_driver?: boolean;
  role_labels?: RosterRoleLabel[];
  active?: boolean;
  notes?: string | null;
}

/** POST /reviews/{id}/attribute body -- replaces the review's UNPAID mention set. */
export interface ReviewAttributeBody {
  person_ids: number[];
}

/** POST /mentions/{id}/paid body -- paid_date is a calendar date (default today). */
export interface ReviewMarkPaidBody {
  paid_date: string; // YYYY-MM-DD
  bonus_amount_usd?: number;
}

/**
 * One row of GET /review-incentives/reports/mentions (per-person mention
 * history: review excerpt, date, state, paid_at, amount). The contract pins
 * the CONTENT, not the envelope -- fields are optional so the views can
 * normalize defensively (unwrapList + fallbacks).
 */
export interface ReviewMentionReportRow {
  mention_id?: number;
  review_id?: number;
  person_id?: number;
  full_name?: string;
  review_excerpt?: string | null;
  review_date?: string | null;
  attribution_state?: string | null;
  paid_at?: string | null;
  bonus_amount_usd?: number | string | null;
  review_link?: string | null;
  /** Waived payment state (contract v1.7.0 — the print view excludes these rows). */
  waived_at?: string | null;
  waived_by?: number | null;
}

/** Per-driver aggregate line for the all-drivers report summary (tolerant). */
export interface ReviewDriverSummaryRow {
  person_id?: number;
  full_name?: string;
  mentions?: number;
  paid?: number;
  /** v1.7.0: excludes waived. */
  unpaid?: number;
  /** Waived-mention count (contract v1.7.0, windowed like paid/unpaid). */
  waived?: number;
  total_paid_usd?: number | string | null;
  last_mention_at?: string | null;
  /** Unconfirmed AI suggestions (contract v1.4.0; never date-windowed). */
  suggested_pending?: number;
}

// ---------------------------------------------------------------------------
// System Config (P12 -- locked contract system-config-api v1.0.0, decision #146)
// ---------------------------------------------------------------------------

/**
 * One managed env key (contract sect 5.1). Secret rows NEVER carry `value`
 * (write-only semantics; masked display = last4 + rotated_at); plain rows
 * carry `value` and edit inline.
 */
export interface SystemConfigKey {
  id: number;
  key_name: string;
  label: string;
  is_secret: boolean;
  set: boolean;
  /** Plaintext tail of a secret for masked display ("f3ab"); secret rows only. */
  last4?: string | null;
  /** Plain-config value; plain rows only -- never present on secret rows. */
  value?: string | null;
  /** Last time the VALUE changed (not metadata). */
  rotated_at?: string | null;
  source: 'portal' | 'imported';
}

/** One provider card = one managed env file (contract sect 3.1). */
export interface SystemConfigProvider {
  provider: string;
  env_file: string;
  /**
   * File hand-edited since last render (sha256 mismatch) -- warn, never
   * block. NULL = never rendered (v1.0.1 sect 3.2: the boolean starts
   * existing at first apply).
   */
  drift: boolean | null;
  last_rendered_at: string | null;
  keys: SystemConfigKey[];
}

/** Per-procedure AI routing row (contract sect 2.2). */
export interface AiProcedure {
  procedure_key: string;
  label: string;
  /** routine = scheduled Claude Code tick; direct = server-side API call. */
  mode: 'routine' | 'direct';
  /** Free text -- the UI offers the curated list but never restricts. */
  model: string;
  /** Stored Anthropic key row id; required by the API only when mode=direct. */
  anthropic_secret_id: number | null;
  notes: string | null;
  updated_at?: string | null;
}

/** GoTo OAuth status block of the masked state (contract sect 5.1). */
export interface GotoOauthStatus {
  token_file_present: boolean;
  token_file_mtime: string | null;
  /**
   * Stored result of the most recent verify probe; null until first run.
   * v1.0.1: the FULL sect 6.5 result rides here ({at, ok, granted_scope,
   * families}) -- a superset of the v1.0.0 {at, ok} example.
   */
  last_verify: {
    at: string;
    ok: boolean;
    granted_scope?: string | null;
    families?: Record<string, GotoVerifyFamily>;
  } | null;
}

/** GET /admin/system-config -- the full masked state (contract sect 5.1). */
export interface SystemConfigState {
  providers: SystemConfigProvider[];
  ai_procedures: AiProcedure[];
  goto_oauth: GotoOauthStatus;
}

/** PUT /secrets/{id} body (sect 5.2): the plaintext is wrapped and dropped. */
export interface SystemConfigSecretPutBody {
  value: string;
}

/** POST /secrets body (sect 5.3): add a NEW managed key to a provider card. */
export interface SystemConfigSecretCreateBody {
  provider: string;
  key_name: string;
  label: string;
  is_secret: boolean;
  value: string;
}

/** One curated Anthropic model entry (GET /models, sect 5.4). */
export interface CuratedModel {
  id: string;
  label: string;
}

export interface CuratedModelsResponse {
  models: CuratedModel[];
  free_text_allowed: boolean;
}

/** PUT /ai-procedures/{procedure_key} body (sect 5 table). */
export interface AiProcedurePutBody {
  mode: 'routine' | 'direct';
  model: string;
  anthropic_secret_id?: number | null;
  notes?: string | null;
}

/** Per-unit pickup row of the apply effectiveness report (contract sect 4). */
export interface ConfigEffectivenessRow {
  unit: string;
  /** 'timer' | 'service' | open set ('external routine' honesty row). */
  kind: string;
  effectiveness: 'next-run' | 'requires-restart' | 'n/a';
  /** From systemctl list-timers parse -- best effort, NULLable. */
  next_run_at?: string | null;
}

/** POST /apply response (contract sect 5.5). */
export interface ConfigApplyResponse {
  rendered: string[];
  effectiveness: ConfigEffectivenessRow[];
}

/** POST /restart-api -> 202 (contract sect 4). */
export interface ConfigRestartResponse {
  restarting: boolean;
}

/** GET /goto/oauth/start -- wizard step 2 payload (contract sect 6.2). */
export interface GotoOauthStart {
  authorize_url: string;
  redirect_uri: string;
}

/** One API-family probe result (contract sect 6.5). */
export interface GotoVerifyFamily {
  ok: boolean;
  status?: number;
  detail?: string | null;
  note?: string | null;
}

/** POST /goto/verify response -- per-family reachability (contract sect 6.5). */
export interface GotoVerifyResponse {
  ok: boolean;
  granted_scope?: string | null;
  families: Record<string, GotoVerifyFamily>;
}

// ---------------------------------------------------------------------------
// Module Settings (contract module-settings-api v1.0.0 sect 4)
// ---------------------------------------------------------------------------

/** One registry entry + its effective state (admin GET, contract sect 4.2).
 *  Values are JSON scalars typed per `type` ('bool' -> boolean, 'int' ->
 *  number); the PUT body sends the same scalar back. */
export interface ModuleSettingItem {
  key: string;                       // namespaced 'module.setting'
  module: string;                    // owning module = page section
  label: string;
  description: string;
  type: 'bool' | 'int';
  validation: { min: number | null; max: number | null } | null;
  client_visible: boolean;
  default: boolean | number;        // resolved (settings-derived keys show live value)
  effective: boolean | number;
  overridden: boolean;               // literally: an override row exists
  updated_by: number | null;
  updated_by_name: string | null;
  updated_at: string | null;
}

/** GET /api/admin/module-settings (contract sect 4.2). */
export interface ModuleSettingsAdminResponse {
  settings: ModuleSettingItem[];
}

/** GET /api/module-settings -- client_visible effective values only
 *  (contract sect 4.1; any authed session). */
export interface ClientModuleSettings {
  settings: Record<string, boolean | number>;
}

// ---------------------------------------------------------------------------
// Team Performance (contract performance-api v1.0.0; decision #180)
// ---------------------------------------------------------------------------

export type PerformanceGrain = 'day' | 'week' | 'month';

/** Vocabulary row: users with activity in either window (stable multiselect). */
export interface PerformanceUser {
  user_id: number;
  name: string;
  person_id: number | null;
  person_name: string | null;
}

/** The seven v1 metrics (contract SS2) -- per-user totals row. */
export interface PerformanceTotals {
  user_id: number;
  reviews_handled: number;
  approved: number;
  edited: number;
  dismissed: number;
  median_ttr_minutes: number | null;
  edit_depth_avg: number | null;
  edited_fields_total: number;
  sla_saves: number;
  /** v1.1.0 metric #8: reviews of drafts naming ANOTHER dispatcher. */
  cross_reviews: number;
  back_entries: number;
  entries_entered: number;
  active_days: number;
}

/** Team aggregate: totals keys minus user_id, plus the ownerless expiry count. */
export type PerformanceTeam = Omit<PerformanceTotals, 'user_id'> & {
  sla_expired: number;
};

/** Zero-filled team bucket (KPI sparklines + the chart ghost). */
export interface PerformanceTeamBucket {
  t: string;
  reviews_handled: number;
  approved: number;
  edited: number;
  dismissed: number;
  sla_saves: number;
  cross_reviews: number;
  back_entries: number;
  entries_entered: number;
  active_users: number;
}

/** SPARSE per-user bucket (chartable count metrics; FE zero-fills). */
export interface PerformanceUserBucket {
  t: string;
  user_id: number;
  reviews_handled: number;
  promoted: number;
  back_entries: number;
  entries_entered: number;
  sla_saves: number;
  cross_reviews: number;
}

/** GET /api/performance/team (contract SS3). */
export interface PerformanceTeamResponse {
  window: { from: string; to: string };
  previous_window: { from: string; to: string };
  grain: PerformanceGrain;
  sla_days: number;
  users: PerformanceUser[];
  team: PerformanceTeam;
  totals: PerformanceTotals[];
  previous: { team: PerformanceTeam; totals: PerformanceTotals[] };
  team_series: PerformanceTeamBucket[];
  previous_team_series: PerformanceTeamBucket[];
  series: PerformanceUserBucket[];
}

// ---------------------------------------------------------------------------
// Engine Performance (contract performance-api v1.2.0 SS7; decisions #180 +
// #185) -- the judge-config analytics surface over ai_engine_configs /
// ai_judgements. Read-only.
// ---------------------------------------------------------------------------

export type EngineVerdict = 'applicable' | 'not_applicable' | 'unclear' | 'error';
export type EngineProcedure = 'assisted_entry_draft' | 'review_attribution';

export interface EngineConfigStats {
  judgements: number;
  recordings: number;
  first_judged_at: string | null;
  last_judged_at: string | null;
  verdicts: Record<EngineVerdict, number>;
  drafts_produced: number;
  era_inferred_rows: number;
}

/** Registry list item (SS7.2; prompt text deliberately absent). */
export interface EngineConfig {
  id: number;
  procedure_key: EngineProcedure;
  prompt_version: string;
  prompt_sha256: string;
  model: string;
  effort: string | null;
  runner: string;
  parent_config_id: number | null;
  notes: string | null;
  first_seen_at: string;
  prompt_chars: number;
  stats: EngineConfigStats;
}

/** Lineage entries carry no stats (list-item shape minus stats). */
export type EngineConfigLite = Omit<EngineConfig, 'stats'>;

export interface EngineAdjTier {
  n: number;
  est_lost_usd: number;
  /** edited tier only: avg top-level key count of normalized field_diffs. */
  edit_depth_avg?: number;
  /** dismissed tier only ('stale' = the SLA sweeper, not a human). */
  by_reason?: Record<string, number>;
}

export interface EngineAdjudication {
  drafts: number;
  adjudicated: number;
  tiers: Record<'pending' | 'approved' | 'edited' | 'dismissed' | 'superseded', EngineAdjTier>;
  precision_gate: { adjudicated: number; threshold: number };
}

/** GET /api/performance/engine/configs/{id} (SS7.2 detail). */
export interface EngineConfigDetail extends EngineConfig {
  prompt_text: string | null;
  lineage: { ancestors: EngineConfigLite[]; children: EngineConfigLite[] };
  adjudication: EngineAdjudication;
}

export interface EngineFlipDraft {
  draft_id: number;
  reason_primary: string | null;
  callback_potential: string | null;
  est_lost_usd: number | null;
  occurred_at: string | null;
  review_state: string;
}

/** GET /api/performance/engine/comparison (SS7.3). */
export interface EngineComparison {
  a: EngineConfig;
  b: EngineConfig;
  head_to_head: {
    corpus_recordings: number;
    corpus_calls: number;
    matrix: { a: EngineVerdict; b: EngineVerdict; n: number }[];
    flips: { legs: number; calls: number; est_lost_usd: number; drafts: EngineFlipDraft[] };
    reverse_flips: { legs: number; calls: number };
  };
  era: {
    confounded: boolean;
    a: { judgements: number; recordings: number; verdicts: Record<EngineVerdict, number>;
         first_judged_at: string | null; last_judged_at: string | null };
    b: { judgements: number; recordings: number; verdicts: Record<EngineVerdict, number>;
         first_judged_at: string | null; last_judged_at: string | null };
  };
  adjudication: { a: EngineAdjudication; b: EngineAdjudication };
}

export interface EngineRatesDay {
  t: string;
  business_day: boolean;
  judged_legs: number;
  judged_calls: number;
  done_legs: number;
  done_rate: number | null;
  drafts: number;
  unclear: number;
  unclear_share: number | null;
  errors: number;
}

/** GET /api/performance/engine/rates (SS7.4; band = doc 71 SS5 served). */
export interface EngineRates {
  window: { from: string; to: string };
  procedure: EngineProcedure;
  band: {
    source: string;
    judged_legs: { business: [number, number]; weekend: [number, number] };
    drafts: { business: [number, number]; weekend: [number, number] };
    done_rate: { business: [number, number] };
    unclear_share_max: number;
  };
  days: EngineRatesDay[];
}
